import {
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { useDispenseAssets } from '@/hooks/useDispenseAssets';

import type { CassetteView } from '@/types/generated/athena';

type Props = {
  index: number;
  value?: CassetteView;
  onChange?: (index: number, value: CassetteView | undefined) => void;
  disabled?: boolean;
};

const CassetteConfigurationCard = ({ index, value, onChange, disabled }: Props) => {
  const { assets } = useDispenseAssets();

  const [asset, setAsset] = useState<string>('');
  const [billId, setBillId] = useState<string | null>(value?.bill_note_id ?? '');
  const [count, setCount] = useState<string>(value?.count ? String(value?.count) : '');

  const { empty, valid } = useMemo(() => {
    const empty = billId === undefined || billId === null;
    const valid = empty || (!!asset && !!billId && !!count);
    return { empty, valid };
  }, [asset, billId, count]);

  const handleAssetChange = (value: string) => {
    setAsset(value);
    setBillId('');
    setCount('');
  };

  const handleBillChange = (value: string) => {
    setBillId(value);
    setCount('');
  };

  const handleEmptyChange = () => {
    setBillId((prev) => (prev !== null ? null : ''));
    setCount('');
  };

  const handleCountChange = (value: string) => setCount(value);

  useEffect(() => {
    const newValue = !valid
      ? undefined
      : {
          cassette_index: index,
          bill_note_id: billId,
          count: count ? Number.parseInt(count) : 0,
        };
    onChange?.(index, newValue);
  }, [valid, count]);

  useEffect(() => {
    if (!assets || !value) return;
    if (value?.bill_note_id) {
      const assetId = assets
        .flatMap(({ bills }) => bills)
        .find((bill) => value?.bill_note_id === bill?.id)?.asset_id;
      if (assetId) setAsset(assetId);
    } else {
      setBillId(null);
    }
  }, [assets]);

  return (
    <Card sx={{ bgcolor: 'neutral.50', p: 2 }} data-test="cassette-config-card">
      <Stack direction="row" justifyContent="space-between" mb={2} alignItems="center">
        <Typography variant="overline" borderLeft={3} pl={1} borderColor="red">
          Cassette {index}
        </Typography>
        <ValidationChip empty={empty} valid={valid} />
      </Stack>
      {!empty && (
        <Grid container mb={2} gap={2}>
          <Grid item flex={1}>
            <FormControl fullWidth>
              <InputLabel variant="standard" size="small">
                Asset
              </InputLabel>
              <Select
                onChange={({ target: { value } }) => handleAssetChange(value!)}
                sx={{ bgcolor: 'white' }}
                disabled={disabled}
                value={asset}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Asset
                </MenuItem>
                {assets?.map(({ name, id }) => (
                  <MenuItem key={id} value={id!}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item flex={1}>
            <FormControl fullWidth>
              <InputLabel variant="standard" size="small">
                Denomination
              </InputLabel>
              <Select
                onChange={({ target: { value } }) => handleBillChange(String(value))}
                disabled={!asset || disabled}
                value={billId}
                sx={{ bgcolor: 'white' }}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Denomination
                </MenuItem>
                {assets
                  ?.find(({ id }) => id === asset)
                  ?.bills?.map(({ id, denomination }) => (
                    <MenuItem key={id!} value={id!}>
                      {denomination}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item flex={1}>
            <FormControl fullWidth>
              <InputLabel variant="standard" size="small">
                Load amount
              </InputLabel>
              <TextField
                onChange={({ target: { value } }) => handleCountChange(value)}
                disabled={!asset || !billId || disabled}
                sx={{ bgcolor: 'white' }}
                placeholder="Amount"
                value={count}
              />
            </FormControl>
          </Grid>
        </Grid>
      )}
      <FormControlLabel
        control={<Checkbox disabled={disabled} checked={empty} onChange={handleEmptyChange} />}
        label="Leave this cassette empty"
      />
    </Card>
  );
};

const ValidationChip = ({ empty, valid }: { empty: boolean; valid: boolean }) => {
  const label = empty ? 'Empty' : valid ? 'Configured' : 'Not Configured';
  const color = empty ? 'default' : valid ? 'success' : 'error';
  return <Chip sx={{ fontWeight: 'bold' }} label={label} size="small" color={color} />;
};

export default CassetteConfigurationCard;
